import React, { useEffect, useRef } from 'react';
import SunEditor from 'suneditor-react';
import katex from 'katex';
import { en } from 'suneditor/src/lang';
import plugins from 'suneditor/src/plugins';

const defaultOptions = {
  previewTemplate: `
    <div style="width:auto; max-width:1136px; min-height:400px; margin:auto; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px;">
      {{contents}}
    </div>
  `,
  buttonList: [
    ['undo', 'redo'],
    ['font', 'fontSize', 'formatBlock'],
    ['paragraphStyle', 'blockquote'],
    ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
    ['fontColor', 'hiliteColor', 'textStyle'],
    ['removeFormat'],
    ['outdent', 'indent'],
    ['align', 'horizontalRule', 'list', 'lineHeight'],
    ['table', 'link', 'image', 'video'],
    ['fullScreen', 'showBlocks', 'codeView'],
    ['preview'],
    ['save'],
    [
      '%1161',
      [
        ['undo', 'redo'],
        [
          ':p-Formats-default.more_paragraph',
          'font',
          'fontSize',
          'formatBlock',
          'paragraphStyle',
          'blockquote',
        ],
        ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
        ['fontColor', 'hiliteColor', 'textStyle'],
        ['removeFormat'],
        ['outdent', 'indent'],
        ['align', 'horizontalRule', 'list', 'lineHeight'],
        [
          '-right',
          ':i-Etc-default.more_vertical',
          'fullScreen',
          'showBlocks',
          'codeView',
          'preview',
        ],
        [
          '-right',
          ':r-Table&Media-default.more_plus',
          'table',
          'link',
          'image',
          'video',
        ],
      ],
    ],
    [
      '%893',
      [
        ['undo', 'redo'],
        [
          ':p-Formats-default.more_paragraph',
          'font',
          'fontSize',
          'formatBlock',
          'paragraphStyle',
          'blockquote',
        ],
        ['bold', 'underline', 'italic', 'strike'],
        [
          ':t-Fonts-default.more_text',
          'subscript',
          'superscript',
          'fontColor',
          'hiliteColor',
          'textStyle',
        ],
        ['removeFormat'],
        ['outdent', 'indent'],
        ['align', 'horizontalRule', 'list', 'lineHeight'],
        [
          '-right',
          ':i-Etc-default.more_vertical',
          'fullScreen',
          'showBlocks',
          'codeView',
          'preview',
        ],
        [
          '-right',
          ':r-Table&Media-default.more_plus',
          'table',
          'link',
          'image',
          'video',
        ],
      ],
    ],
    [
      '%855',
      [
        ['undo', 'redo'],
        [
          ':p-Formats-default.more_paragraph',
          'font',
          'fontSize',
          'formatBlock',
          'paragraphStyle',
          'blockquote',
        ],
        [
          ':t-Fonts-default.more_text',
          'bold',
          'underline',
          'italic',
          'strike',
          'subscript',
          'superscript',
          'fontColor',
          'hiliteColor',
          'textStyle',
        ],
        ['removeFormat'],
        ['outdent', 'indent'],
        ['align', 'horizontalRule', 'list', 'lineHeight'],
        [':r-Table&Media-default.more_plus', 'table', 'link', 'image', 'video'],
        [
          '-right',
          ':i-Etc-default.more_vertical',
          'fullScreen',
          'showBlocks',
          'codeView',
          'preview',
        ],
      ],
    ],
    [
      '%563',
      [
        ['undo', 'redo'],
        [
          ':p-Formats-default.more_paragraph',
          'font',
          'fontSize',
          'formatBlock',
          'paragraphStyle',
          'blockquote',
        ],
        [
          ':t-Fonts-default.more_text',
          'bold',
          'underline',
          'italic',
          'strike',
          'subscript',
          'superscript',
          'fontColor',
          'hiliteColor',
          'textStyle',
        ],
        ['removeFormat'],
        ['outdent', 'indent'],
        [
          ':e-List&Line-default.more_horizontal',
          'align',
          'horizontalRule',
          'list',
          'lineHeight',
        ],
        [':r-Table&Media-default.more_plus', 'table', 'link', 'image', 'video'],
        [
          '-right',
          ':i-Etc-default.more_vertical',
          'fullScreen',
          'showBlocks',
          'codeView',
          'preview',
        ],
      ],
    ],
    [
      '%458',
      [
        ['undo', 'redo'],
        [
          ':p-Formats-default.more_paragraph',
          'font',
          'fontSize',
          'formatBlock',
          'paragraphStyle',
          'blockquote',
        ],
        [
          ':t-Fonts-default.more_text',
          'bold',
          'underline',
          'italic',
          'strike',
          'subscript',
          'superscript',
          'fontColor',
          'hiliteColor',
          'textStyle',
          'removeFormat',
        ],
        [
          ':e-List&Line-default.more_horizontal',
          'outdent',
          'indent',
          'align',
          'horizontalRule',
          'list',
          'lineHeight',
        ],
        [':r-Table&Media-default.more_plus', 'table', 'link', 'image', 'video'],
        [
          '-right',
          ':i-Etc-default.more_vertical',
          'fullScreen',
          'showBlocks',
          'codeView',
          'preview',
        ],
      ],
    ],
  ],
  attributesWhitelist: {
    all: 'style|data-.+',
    input: 'checked|name',
    '???': '*',
  },
  defaultStyle:
    "font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 14px; color: #111827; line-height: 1.6;",
  plugins: plugins,
  lang: en,
  fullPage: true,
  stickyToolbar: 0,
  katex: katex,
  width: '100%',
  height: 'auto',
  minHeight: '200px',
  imageMultipleFile: true,
  strictMode: false,
  iframe: true,
  iframeCSSFileName: [
    'https://cdn.jsdelivr.net/npm/suneditor/dist/css/suneditor.min.css',
  ],
  iframeAttributes: { scrolling: 'yes' },
};

const SunEditorComponent = ({
  value,
  onChange,
  options = {},
  justUpdatedValue,
}) => {
  const editorRef = useRef(null);
  const wrapperRef = useRef(null);

  // Strip native browser title tooltips from all toolbar buttons
  // so only SunEditor's own .se-tooltip-inner shows — no double tooltip
  const stripNativeTooltips = () => {
    if (!wrapperRef.current) return;
    wrapperRef.current
      .querySelectorAll('.se-toolbar button[title], .se-toolbar [title]')
      .forEach((el) => {
        el.setAttribute('data-se-title', el.getAttribute('title'));
        el.removeAttribute('title');
      });
  };

  useEffect(() => {
    if (editorRef.current) {
      try {
        if (value !== editorRef.current.getContents()) {
          editorRef.current.setContents(value || '');
        }
      } catch (e) {
        // iframe not ready yet
      }
    }
    // Small delay so the toolbar is fully rendered before we strip titles
    const t = setTimeout(stripNativeTooltips, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (editorRef.current && typeof justUpdatedValue === 'string') {
      editorRef.current.setContents(justUpdatedValue);
    }
  }, [justUpdatedValue]);

  return (
    <div className="sun-editor-wrapper" ref={wrapperRef}>
      <style>{`
        /* ─── Container ─────────────────────────────────── */
        .sun-editor-wrapper {
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid #e4e4e7;
          background: #ffffff;
          box-shadow: 0 1px 3px rgba(0,0,0,0.07);
        }
        .sun-editor-wrapper .sun-editor {
          border: none !important;
          background: #ffffff !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        /* ─── Toolbar ────────────────────────────────────── */
        .sun-editor-wrapper .se-toolbar {
          background: #f9fafb !important;
          border-bottom: 1px solid #e4e4e7 !important;
          border-top: none !important;
          padding: 4px 6px !important;
          box-shadow: none !important;
          line-height: 1 !important;
        }
        .sun-editor-wrapper .se-btn-module {
          border-right: 1px solid #e9eaec !important;
          margin-right: 2px !important;
          padding-right: 2px !important;
        }
        .sun-editor-wrapper .se-btn-module:last-child {
          border-right: none !important;
        }

        /* ─── Toolbar buttons ────────────────────────────── */
        .sun-editor-wrapper .se-btn,
        .sun-editor-wrapper .se-toolbar button {
          width: 28px !important;
          height: 28px !important;
          min-width: 28px !important;
          border-radius: 5px !important;
          border: none !important;
          background: transparent !important;
          color: #374151 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 0 !important;
          margin: 1px !important;
          cursor: pointer !important;
          transition: background 0.1s, color 0.1s !important;
          font-size: 11px !important;
          line-height: 1 !important;
          vertical-align: middle !important;
        }
        .sun-editor-wrapper .se-btn:hover,
        .sun-editor-wrapper .se-toolbar button:hover {
          background: #e5e7eb !important;
          color: #111827 !important;
        }
        .sun-editor-wrapper .se-btn.active,
        .sun-editor-wrapper .se-toolbar button.active {
          background: #dbeafe !important;
          color: #1d4ed8 !important;
        }

        /* ─── SVG icons — solid/filled ──────────────────── */
        .sun-editor-wrapper .se-btn svg,
        .sun-editor-wrapper .se-toolbar button svg,
        .sun-editor-wrapper .se-btn svg *,
        .sun-editor-wrapper .se-toolbar button svg * {
          width: 14px !important;
          height: 14px !important;
          fill: #52525b !important;
          stroke: none !important;
          stroke-width: 0 !important;
          pointer-events: none !important;
        }
        /* Root svg size must be set on the element itself */
        .sun-editor-wrapper .se-btn svg,
        .sun-editor-wrapper .se-toolbar button svg {
          width: 14px !important;
          height: 14px !important;
        }
        .sun-editor-wrapper .se-btn:hover svg *,
        .sun-editor-wrapper .se-toolbar button:hover svg * {
          fill: #18181b !important;
          stroke: none !important;
        }
        .sun-editor-wrapper .se-btn.active svg *,
        .sun-editor-wrapper .se-toolbar button.active svg * {
          fill: #1d4ed8 !important;
          stroke: none !important;
        }
        /* Explicitly kill any stroke that SunEditor sets inline */
        .sun-editor-wrapper .se-btn svg path,
        .sun-editor-wrapper .se-btn svg rect,
        .sun-editor-wrapper .se-btn svg circle,
        .sun-editor-wrapper .se-btn svg line,
        .sun-editor-wrapper .se-btn svg polyline,
        .sun-editor-wrapper .se-btn svg polygon {
          fill: inherit !important;
          stroke: none !important;
        }

        /* ─── TOOLTIP — Kill the default, keep ONE ───────── */
        /*
          SunEditor wraps each button in a .se-tooltip span.
          The ::after / ::before pseudo-elements on .se-tooltip are
          the "arrow" and "bubble" of the NATIVE se-tooltip.
          We hide ALL of those and instead style the .se-tooltip-inner
          element that SunEditor inserts as a real DOM node.
        */

        /* 1. Hide SunEditor's CSS-only pseudo-element tooltip */
        .sun-editor-wrapper .se-tooltip::after,
        .sun-editor-wrapper .se-tooltip::before {
          display: none !important;
          content: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }

        /* 2. Also hide the wrapper that animates the pseudo tooltip */
        .sun-editor-wrapper .se-tooltip-inner {
          display: none !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }

        /* 3. Style the REAL tooltip element SunEditor injects as sibling */
        .sun-editor-wrapper .se-tooltip-content {
          position: absolute !important;
          z-index: 9999 !important;
          bottom: calc(100% + 6px) !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          background: #1f2937 !important;
          color: #f9fafb !important;
          font-size: 11px !important;
          font-weight: 500 !important;
          line-height: 1.3 !important;
          padding: 4px 8px !important;
          border-radius: 5px !important;
          white-space: nowrap !important;
          pointer-events: none !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.25) !important;
          border: none !important;
          /* arrow */
        }
        .sun-editor-wrapper .se-tooltip-content::after {
          content: '' !important;
          position: absolute !important;
          top: 100% !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          border: 4px solid transparent !important;
          border-top-color: #1f2937 !important;
        }

        /* ─── Select dropdowns (Font, Size, Format) ─────── */
        .sun-editor-wrapper .se-btn-select {
          height: 28px !important;
          border-radius: 5px !important;
          border: 1px solid #e4e4e7 !important;
          background: #ffffff !important;
          color: #374151 !important;
          font-size: 11px !important;
          padding: 0 6px !important;
          white-space: nowrap !important;
        }
        .sun-editor-wrapper .se-btn-select:hover {
          background: #f3f4f6 !important;
          border-color: #d1d5db !important;
        }
        .sun-editor-wrapper .se-btn-select .txt {
          color: #374151 !important;
          font-size: 11px !important;
        }

        /* ─── Dropdown list panels ───────────────────────── */
        .sun-editor-wrapper .se-list-layer,
        .sun-editor-wrapper .se-selector-font,
        .sun-editor-wrapper .se-selector-font-size {
          background: #ffffff !important;
          border: 1px solid #e4e4e7 !important;
          border-radius: 8px !important;
          box-shadow: 0 4px 14px rgba(0,0,0,0.10) !important;
          padding: 4px !important;
          z-index: 10000 !important;
        }
        .sun-editor-wrapper .se-list-layer li button,
        .sun-editor-wrapper .se-list-inner li button {
          color: #374151 !important;
          font-size: 12px !important;
          border-radius: 4px !important;
          padding: 5px 8px !important;
          width: 100% !important;
          text-align: left !important;
        }
        .sun-editor-wrapper .se-list-layer li button:hover,
        .sun-editor-wrapper .se-list-inner li button:hover {
          background: #f3f4f6 !important;
          color: #111827 !important;
        }
        .sun-editor-wrapper .se-list-layer li.active button {
          background: #eff6ff !important;
          color: #1d4ed8 !important;
        }

        /* ─── Writing area ───────────────────────────────── */
        .sun-editor-wrapper .se-wrapper,
        .sun-editor-wrapper .se-wrapper-inner {
          background: #ffffff !important;
        }
        .sun-editor-wrapper .sun-editor-editable {
          background: #ffffff !important;
          color: #111827 !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
          font-size: 14px !important;
          line-height: 1.65 !important;
          padding: 16px 20px !important;
          caret-color: #111827 !important;
        }

        /* ─── Code view ──────────────────────────────────── */
        .sun-editor-wrapper .se-wrapper-code {
          background: #f8fafc !important;
          color: #1e293b !important;
          font-family: 'Fira Code', 'Cascadia Code', Consolas, monospace !important;
          font-size: 13px !important;
          padding: 16px !important;
          border-top: 1px solid #e4e4e7 !important;
        }

        /* ─── Resize bar ─────────────────────────────────── */
        .sun-editor-wrapper .se-resizing-bar {
          background: #f9fafb !important;
          border-top: 1px solid #e4e4e7 !important;
          height: 8px !important;
        }
        .sun-editor-wrapper .se-resizing-bar .se-navigation {
          color: #9ca3af !important;
          font-size: 11px !important;
        }

        /* ─── Link / Image dialogs ───────────────────────── */
        .sun-editor-wrapper .se-dialog-inner {
          background: #ffffff !important;
          border-radius: 10px !important;
          border: 1px solid #e4e4e7 !important;
          box-shadow: 0 8px 32px rgba(0,0,0,0.12) !important;
          overflow: hidden !important;
        }
        .sun-editor-wrapper .se-dialog-title {
          background: #f9fafb !important;
          border-bottom: 1px solid #e4e4e7 !important;
          color: #111827 !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          padding: 12px 16px !important;
        }
        .sun-editor-wrapper .se-dialog-body {
          padding: 16px !important;
          color: #374151 !important;
        }
        .sun-editor-wrapper .se-dialog-form label {
          font-size: 12px !important;
          color: #6b7280 !important;
          font-weight: 500 !important;
          margin-bottom: 4px !important;
          display: block !important;
        }
        .sun-editor-wrapper .se-dialog-form input,
        .sun-editor-wrapper .se-dialog-form select,
        .sun-editor-wrapper .se-dialog-form textarea {
          border: 1px solid #e4e4e7 !important;
          border-radius: 6px !important;
          background: #ffffff !important;
          color: #111827 !important;
          font-size: 13px !important;
          padding: 6px 10px !important;
          width: 100% !important;
          outline: none !important;
        }
        .sun-editor-wrapper .se-dialog-form input:focus,
        .sun-editor-wrapper .se-dialog-form select:focus {
          border-color: #6366f1 !important;
          box-shadow: 0 0 0 2px rgba(99,102,241,0.15) !important;
        }
        .sun-editor-wrapper .se-dialog-footer {
          background: #f9fafb !important;
          border-top: 1px solid #e4e4e7 !important;
          padding: 10px 16px !important;
          display: flex !important;
          gap: 8px !important;
          justify-content: flex-end !important;
        }
        .sun-editor-wrapper .se-btn-primary,
        .sun-editor-wrapper .se-dialog button.se-btn-primary {
          background: #18181b !important;
          color: #f4f4f5 !important;
          border: none !important;
          border-radius: 6px !important;
          padding: 6px 14px !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          cursor: pointer !important;
        }
        .sun-editor-wrapper .se-btn-primary:hover,
        .sun-editor-wrapper .se-dialog button.se-btn-primary:hover {
          background: #27272a !important;
        }

        /* ─── Table in editable area ─────────────────────── */
        .sun-editor-wrapper .sun-editor-editable table {
          border-collapse: collapse !important;
          width: 100% !important;
        }
        .sun-editor-wrapper .sun-editor-editable td,
        .sun-editor-wrapper .sun-editor-editable th {
          border: 1px solid #e4e4e7 !important;
          padding: 8px 12px !important;
        }
        .sun-editor-wrapper .sun-editor-editable th {
          background: #f9fafb !important;
          font-weight: 600 !important;
          color: #374151 !important;
        }

        /* ─── Blockquote ─────────────────────────────────── */
        .sun-editor-wrapper .sun-editor-editable blockquote {
          border-left: 3px solid #d1d5db !important;
          margin: 8px 0 !important;
          padding: 4px 12px !important;
          color: #6b7280 !important;
          background: #f9fafb !important;
        }

        /* ─── More-group collapsed button ────────────────── */
        .sun-editor-wrapper .se-btn-module-border {
          border: 1px solid #e4e4e7 !important;
          border-radius: 6px !important;
          background: #ffffff !important;
        }
        .sun-editor-wrapper .se-btn-module-border:hover {
          background: #f3f4f6 !important;
        }

        /* ─── Scrollbar inside list panels ───────────────── */
        .sun-editor-wrapper .se-list-inner::-webkit-scrollbar { width: 4px; }
        .sun-editor-wrapper .se-list-inner::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 4px;
        }
      `}</style>

      <SunEditor
        getSunEditorInstance={(editor) => (editorRef.current = editor)}
        setOptions={{ ...defaultOptions, ...options }}
        onChange={onChange}
        placeholder="Write you content here..."
      />
    </div>
  );
};

export default SunEditorComponent;
